import React from 'react';

// The easiest rung of the served administration. response_window_ms decays from
// 7000ms to 1200ms as the ladder climbs, so this constant is only ever reached
// when the block carries no params at all.
const DEFAULT_WINDOW_MS = 7000;

// This instrument serves no inter-stimulus interval. The fixation and the gap
// are fixed administration constants that sit ON TOP of the served response
// window. Flanker and stroop fit their fixation INSIDE a served isi_ms; that
// convention belongs to those instruments and is not imported here.
const FIXATION_MS = 300;
const GAP_MS = 200;

// The server sends the literal string "x" for multiplication. Rendering it as
// the letter x reads as an algebraic variable next to two numerals.
const OPERATOR_GLYPH = { '+': '+', x: '×' };

// The spoken form of the same operator. A screen-reader user hears the
// expression exactly as a sighted participant reads it — never its value.
const OPERATOR_WORD = { '+': 'plus', x: 'times' };

// Renders the served expression and nothing else. The operands appear in the
// order served (they are not reordered on the theory that the operation
// commutes), no digit is annotated, and nothing here is computed from them:
// working out whether the proposal is right is the task.
function StimulusView({ stimulus, compact }) {
  const { left, operator, right, proposed } = stimulus;
  const glyph = OPERATOR_GLYPH[operator] || operator;
  const word = OPERATOR_WORD[operator] || operator;
  return (
    <span
      role="img"
      aria-label={`${left} ${word} ${right} equals ${proposed}?`}
      style={{
        fontSize: compact ? 16 : 'clamp(40px, 11vw, 96px)',
        fontWeight: 800,
        letterSpacing: '-0.02em',
        color: 'var(--text-primary)',
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
      }}
    >
      {left} {glyph} {right} = {proposed}
    </span>
  );
}

export const timedArithmeticTask = {
  id: 'timed-arithmetic',
  name: 'Quick Sums',
  pillar: 'problem',

  // Forced choice: every trial ends in a response, so trial length carries no
  // information about what the participant did and there is no rhythm to leak.
  // The trial may end as soon as an answer lands.
  holdsFullWindow: false,

  instructions: [
    'An equation appears with an answer already filled in.',
    'Say whether that answer is right or wrong.',
    'Answer before the timer runs out — a late answer counts as wrong.',
  ],
  // Negative LEFT, affirmative RIGHT — the battery-wide convention, so a
  // participant moving between instruments never has to re-learn which side
  // means "yes". ResponseTargets renders this array left-to-right with no
  // reordering, so the keys and hint belong to the POSITION, not to the id:
  // ArrowLeft/f must always sit on the left button whatever it answers.
  targets: [
    { id: 'false', label: 'Wrong', keys: ['ArrowLeft', 'f'], hint: 'F · ←' },
    { id: 'true', label: 'Right', keys: ['ArrowRight', 'j'], hint: 'J · →' },
  ],

  // Listed in target order, so the card reads in the same sequence as the two
  // buttons underneath it.
  examples: [
    { stimulus: { left: 7, operator: 'x', right: 8, proposed: 54 }, response: 'Wrong' },
    { stimulus: { left: 4, operator: '+', right: 5, proposed: 9 }, response: 'Right' },
  ],

  toAnswer: (targetId) => ({ isTrue: targetId === 'true' }),

  // Every trial is judged. No acceptsResponse predicate is declared, so the
  // runner treats all of them as responsive — there is no prime, no lead-in and
  // no unjudged trial on this instrument, and a client trial carries nothing
  // that could classify one.
  //
  // The server marks a judgement later than response_window_ms INCORRECT, so
  // the client window must equal it: the runner bounds reaction time at
  // windowMs + 250, and a window that fell back to the default would let a
  // response the server will grade as wrong be recorded as a real one.
  // operand_max is the other difficulty knob and is already visible as the
  // operands themselves. Number() because a string window would make that
  // ceiling concatenate rather than add, and `||` rather than `??` so a served
  // zero falls back instead of timing every trial out instantly.
  timing: (params) => ({
    fixationMs: FIXATION_MS,
    windowMs: Number(params?.response_window_ms) || DEFAULT_WINDOW_MS,
    gapMs: GAP_MS,
  }),

  // Feedback only, and only ever after a response has landed or the window has
  // closed — the runner calls this from respond() and endTrial(), never from a
  // render. Nothing hidden is being read: both operands, the operator and the
  // proposal are all on screen, and doing this arithmetic IS the exercise.
  // Whitelisted, and it fails CLOSED. This task declares no
  // `feedbackAvailable: false` and runs under a driver that feeds back on every
  // trial, so whatever this returns is shown to the learner as Correct or Not
  // quite. Treating "anything that is not +" as multiplication meant a served
  // `-` or `/` would keep rendering correctly — the glyph map already falls back
  // gracefully — while every single trial was fed back WRONG, with no other
  // symptom anywhere. An unrecognised operator now yields null, which the runner
  // reads as "no expectation": no feedback rather than confident nonsense.
  practiceExpectation: (stimulus) => {
    const { left, operator, right, proposed } = stimulus;
    if (operator !== '+' && operator !== 'x') return null;
    const actual = operator === '+' ? left + right : left * right;
    return actual === proposed ? 'true' : 'false';
  },

  StimulusView,
};
